import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    MapPin, Calendar, Clock, ArrowLeft, User, Phone,
    Mail, CreditCard, CheckCircle, Info, Home
} from 'lucide-react';
import { format } from 'date-fns';
import DefaultSEO from '../components/DefaultSEO';

const BookingDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    useEffect(() => {
        const fetchBookingDetails = async () => {
            try {
                const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8001/api/v1';
                const cleanApiBase = apiBase.replace(/\/+$/, '');
                const token = localStorage.getItem('auth_token');

                if (!token) {
                    setError('Please login to view your booking.');
                    setLoading(false);
                    return;
                }

                const response = await fetch(`${cleanApiBase}/bookings/${id}`, {
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });

                const data = await response.json();
                if (data.success) {
                    setBooking(data.data);
                } else {
                    setError(data.message || 'Failed to load booking details.');
                }
            } catch (err) {
                console.error("Fetch booking error:", err);
                setError('An error occurred while loading booking details.');
            } finally {
                setLoading(false);
            }
        };

        fetchBookingDetails();
    }, [id]);

    const handleCancelBooking = () => {
        setShowCancelModal(true);
    };

    const confirmCancelBooking = async () => {
        setIsCancelling(true);
        try {
            const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1';
            const cleanApiBase = apiBase.replace(/\/+$/, '');
            const token = localStorage.getItem('auth_token');

            const response = await fetch(`${cleanApiBase}/bookings/${booking.id}/cancel`, {
                method: 'PATCH',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();
            if (data.success) {
                setBooking({ ...booking, status: 'cancelled' });
                setShowCancelModal(false);
            } else {
                alert(data.message || 'Failed to cancel booking.');
            }
        } catch (err) {
            console.error(err);
            alert('An error occurred while cancelling the booking.');
        } finally {
            setIsCancelling(false);
        }
    };

    const getPropertyImage = (property) => {
        if (property?.images?.[0]?.file_path) {
            const path = property.images[0].file_path;
            if (path.startsWith('http')) {
                return path;
            }
            const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1';
            try {
                const url = new URL(apiBase);
                return `${url.protocol}//${url.host}/storage/${path}`;
            } catch {
                return `${process.env.REACT_APP_STORAGE_URL || (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/api\/v1\/?$/, '') + '/storage'}/${path}`;
            }
        }
        return 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80';
    };

    const formatDateSafe = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'dd MMM yyyy');
        } catch {
            return dateString;
        }
    };

    const calculateNights = (checkIn, checkOut) => {
        if (!checkIn || !checkOut) return 1;
        try {
            const diffTime = Math.abs(new Date(checkOut) - new Date(checkIn));
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays || 1;
        } catch {
            return 1;
        }
    };

    const getStatusStyles = (status) => {
        const cleanStatus = (status || 'pending').toLowerCase();
        switch (cleanStatus) {
            case 'confirmed':
            case 'paid':
                return {
                    badge: 'bg-success bg-opacity-10 text-success border-success',
                    solidBadge: 'bg-success text-white border-success'
                };
            case 'completed':
                return {
                    badge: 'bg-primary bg-opacity-10 text-primary border-primary',
                    solidBadge: 'bg-primary text-white border-primary'
                };
            case 'cancelled':
                return {
                    badge: 'bg-danger bg-opacity-10 text-danger border-danger',
                    solidBadge: 'bg-danger text-white border-danger'
                };
            default:
                return {
                    badge: 'bg-warning bg-opacity-10 text-warning-emphasis border-warning',
                    solidBadge: 'bg-warning text-dark border-warning'
                };
        }
    };

    if (loading) {
        return (
            <div className="container py-5 min-vh-100 d-flex justify-content-center align-items-center">
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container py-5 min-vh-100">
                <div className="alert alert-danger rounded-4 shadow-sm border-0 py-3 d-flex align-items-center gap-2">
                    <Info size={20} />
                    {error}
                </div>
                <button onClick={() => navigate('/account/bookings')} className="btn btn-outline-primary d-inline-flex align-items-center gap-2 mt-3">
                    <ArrowLeft size={18} /> Back to Bookings
                </button>
            </div>
        );
    }

    if (!booking) return null;

    const property = booking.property || {};
    const statusStyle = getStatusStyles(booking.status);
    const paymentStatusStyle = getStatusStyles(booking.payment_status);
    const totalNights = calculateNights(booking.check_in_date, booking.check_out_date);

    const totalPrice = parseFloat(booking.total_price || 0);
    const basePrice = totalPrice / 1.18;
    const taxAmount = totalPrice - basePrice;
    const pricePerNight = basePrice / (totalNights || 1);

    return (
        <React.Fragment>
            <DefaultSEO title="Booking Details | ZoyoTrip" />
            <div className="container py-3 py-md-5 min-vh-100 bg-light">
                <div className="mb-4">
                    <h2 className="fw-bold text-dark mb-0 fs-3 fs-md-2">Booking Details</h2>
                    <p className="text-muted mb-0 font-monospace small" style={{ fontSize: '11px' }}>Booking ID: {booking.booking_id}</p>
                </div>

                <div className="row g-4">
                    {/* Left Column - Hotel & Stay Info */}
                    <div className="col-lg-8">
                        {/* Hotel Info Card */}
                        <div className="card shadow-sm border-0 rounded-4 overflow-hidden mb-4 mobile-list-section">
                            <div className="position-relative property-detail-img-wrapper">
                                <img
                                    src={getPropertyImage(property)}
                                    alt={property.name}
                                    className="w-100 h-100 object-fit-cover"
                                />
                                <div className="position-absolute top-0 end-0 m-3">
                                    <span className={`badge border ${statusStyle.solidBadge} px-3 py-2 rounded-pill fw-bold text-capitalize shadow-sm`} style={{ fontSize: '12px' }}>
                                        {booking.status}
                                    </span>
                                </div>
                            </div>
                            <div className="card-body p-3 p-md-4">
                                <h3 className="fw-bold text-dark mb-2 fs-4 fs-md-3">{property.name || 'Hotel Name'}</h3>
                                <p className="text-muted d-flex align-items-center gap-1 mb-4 small">
                                    <MapPin size={16} className="text-primary flex-shrink-0" /> {[property?.address, property?.city, property?.state].filter(Boolean).join(', ') || 'Location not available'}
                                </p>

                                <hr className="opacity-25 my-3 my-md-4" />

                                <h5 className="fw-bold mb-3 d-flex align-items-center gap-2 fs-5">
                                    <Calendar size={18} className="text-primary" /> Stay Details
                                </h5>

                                <div className="row g-2">
                                    <div className="col-6 col-md-5">
                                        <div className="bg-light p-2.5 p-md-3 rounded-3 h-100 border">
                                            <small className="text-muted d-block mb-1" style={{ fontSize: '11px' }}>Check-in</small>
                                            <strong className="d-block fs-6 fs-md-5 text-dark">{formatDateSafe(booking.check_in_date)}</strong>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-2 d-none d-md-flex align-items-center justify-content-center py-2 order-last order-md-2">
                                        <div className="badge bg-primary bg-opacity-10 text-primary border rounded-pill px-3 py-2 d-flex flex-column align-items-center gap-1">
                                            <Clock size={16} />
                                            <span className="small">{totalNights} Night{totalNights > 1 ? 's' : ''}</span>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-5">
                                        <div className="bg-light p-2.5 p-md-3 rounded-3 h-100 border">
                                            <small className="text-muted d-block mb-1" style={{ fontSize: '11px' }}>Check-out</small>
                                            <strong className="d-block fs-6 fs-md-5 text-dark">{formatDateSafe(booking.check_out_date)}</strong>
                                        </div>
                                    </div>
                                    <div className="col-12 d-flex d-md-none mt-2 justify-content-start">
                                        <div className="badge bg-primary bg-opacity-10 text-primary border rounded-pill px-3 py-1.5 d-inline-flex align-items-center gap-1.5">
                                            <Clock size={13} />
                                            <span style={{ fontSize: '11px' }}>{totalNights} Night{totalNights > 1 ? 's' : ''} Stay</span>
                                        </div>
                                    </div>
                                </div>

                                {booking.room_details && (
                                    <div className="mt-4 bg-light p-3 rounded-3 border">
                                        <strong className="d-flex align-items-center gap-2 mb-2 fs-6 text-dark"><Home size={18} className="text-primary" /> Room Information</strong>
                                        <p className="mb-0 text-muted small">{booking.room_details}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Guest Info Card */}
                        <div className="card shadow-sm border-0 rounded-4 mb-4 mobile-list-section">
                            <div className="card-body p-3 p-md-4">
                                <h5 className="fw-bold mb-4 d-flex align-items-center gap-2 fs-5">
                                    <User size={18} className="text-primary" /> Guest Information
                                </h5>

                                <div className="row g-3 g-md-4">
                                    <div className="col-6 col-md-6">
                                        <div className="d-flex align-items-start gap-2 gap-md-3">
                                            <div className="bg-light p-1.5 p-md-2 rounded-circle" style={{ flexShrink: 0 }}>
                                                <User size={16} className="text-muted" />
                                            </div>
                                            <div>
                                                <small className="text-muted d-block" style={{ fontSize: '11px' }}>Lead Guest</small>
                                                <strong style={{ fontSize: '13px', display: 'block' }} className="text-dark">{booking.guest_firstName} {booking.guest_lastName}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-6">
                                        <div className="d-flex align-items-start gap-2 gap-md-3">
                                            <div className="bg-light p-1.5 p-md-2 rounded-circle" style={{ flexShrink: 0 }}>
                                                <User size={16} className="text-muted" />
                                            </div>
                                            <div>
                                                <small className="text-muted d-block" style={{ fontSize: '11px' }}>Total Guests</small>
                                                <strong style={{ fontSize: '13px', display: 'block' }} className="text-dark">{booking.no_of_guests} Guest{booking.no_of_guests > 1 ? 's' : ''}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-6">
                                        <div className="d-flex align-items-start gap-2 gap-md-3">
                                            <div className="bg-light p-1.5 p-md-2 rounded-circle" style={{ flexShrink: 0 }}>
                                                <Mail size={16} className="text-muted" />
                                            </div>
                                            <div className="w-100 overflow-hidden">
                                                <small className="text-muted d-block" style={{ fontSize: '11px' }}>Email</small>
                                                <strong style={{ fontSize: '13px', display: 'block', wordBreak: 'break-all' }} className="text-dark text-truncate" title={booking.guest_email}>{booking.guest_email || 'N/A'}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-6">
                                        <div className="d-flex align-items-start gap-2 gap-md-3">
                                            <div className="bg-light p-1.5 p-md-2 rounded-circle" style={{ flexShrink: 0 }}>
                                                <Phone size={16} className="text-muted" />
                                            </div>
                                            <div>
                                                <small className="text-muted d-block" style={{ fontSize: '11px' }}>Phone Number</small>
                                                <strong style={{ fontSize: '13px', display: 'block' }} className="text-dark">{booking.guest_phone || 'N/A'}</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* {booking.special_requests && (
                                    <>
                                        <hr className="opacity-25 my-4" />
                                        <h6 className="fw-bold mb-2 small text-dark">Special Requests</h6>
                                        <p className="text-muted bg-light p-3 rounded-3 border mb-0 small">
                                            {booking.special_requests}
                                        </p>
                                    </>
                                )} */}
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Payment Summary */}
                    <div className="col-lg-4">
                        <div className="card shadow-sm border-0 rounded-4 sticky-top mobile-list-section" style={{ top: '100px', zIndex: 10 }}>
                            <div className="card-body p-3 p-md-4">
                                <h5 className="fw-bold mb-4 d-flex align-items-center gap-2 fs-5">
                                    <CreditCard size={18} className="text-primary" /> Payment Summary
                                </h5>

                                <div className="d-flex justify-content-between mb-2.5 align-items-center">
                                    <span className="text-muted small">Base Price ({totalNights} Night{totalNights > 1 ? 's' : ''})</span>
                                    <span className="text-dark fw-semibold">₹{Math.round(basePrice).toLocaleString('en-IN')}</span>
                                </div>

                                <div className="d-flex justify-content-between mb-2.5 align-items-center">
                                    <span className="text-muted small">Avg. Price per Night</span>
                                    <span className="text-dark fw-semibold">₹{Math.round(pricePerNight).toLocaleString('en-IN')}</span>
                                </div>

                                <div className="d-flex justify-content-between mb-3 align-items-center">
                                    <span className="text-muted small">GST / Taxes (18%)</span>
                                    <span className="text-dark fw-semibold">₹{Math.round(taxAmount).toLocaleString('en-IN')}</span>
                                </div>

                                <hr className="opacity-25 my-3" />

                                <div className="d-flex justify-content-between mb-3 align-items-center">
                                    <span className="text-dark fw-bold fs-6">Total Price</span>
                                    <strong className="fs-4 text-dark">₹{Math.round(totalPrice).toLocaleString('en-IN')}</strong>
                                </div>

                                <hr className="opacity-25 my-3" />

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <span className="text-muted small">Payment Method</span>
                                    <span className="text-dark fw-semibold text-capitalize">
                                        {booking.payment_method === 'pay_at_hotel' ? 'Pay at Hotel' : 'Online'}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <span className="text-muted small">Payment Status</span>
                                    <span className={`badge border ${paymentStatusStyle.badge} px-3 py-2 rounded-pill fw-bold text-capitalize`} style={{ fontSize: '12px' }}>
                                        {booking.payment_status || 'Pending'}
                                    </span>
                                </div>

                                {(booking.payment_status?.toLowerCase() === 'completed' || booking.payment_status?.toLowerCase() === 'paid') && (
                                    <div className="alert alert-success bg-success bg-opacity-10 border-0 rounded-3 d-flex gap-2 mb-0">
                                        <CheckCircle size={18} className="text-success flex-shrink-0 mt-0.5" />
                                        <small className="text-success-emphasis" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                            Your payment for this booking has been successfully processed.
                                        </small>
                                    </div>
                                )}

                                {booking.status?.toLowerCase() === 'cancelled' && parseFloat(booking.refund_amount || 0) > 0 && (
                                    <>
                                        <hr className="opacity-25 my-3" />
                                        <div className="d-flex justify-content-between mb-2.5 align-items-center">
                                            <span className="text-muted small">Refund Amount</span>
                                            <span className="text-success fw-bold">₹{parseFloat(booking.refund_amount).toLocaleString('en-IN')}</span>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-muted small">Refund Status</span>
                                            <span className={`badge border ${booking.refund_status === 'processed' ? 'bg-success text-white' : 'bg-warning text-dark'} px-3 py-2 rounded-pill fw-bold text-capitalize`} style={{ fontSize: '12px' }}>
                                                {booking.refund_status || 'Pending'}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {booking.status?.toLowerCase() !== 'cancelled' && booking.status?.toLowerCase() !== 'completed' && (
                    <div className="d-flex justify-content-center mt-2">
                        <button
                            onClick={handleCancelBooking}
                            className="btn btn-outline-danger px-4 py-2.5 rounded-pill fw-bold shadow-sm w-100"
                            style={{ maxWidth: '320px', fontSize: '15px' }}
                        >
                            Cancel Booking
                        </button>
                    </div>
                )}
            </div>

            <style>{`
                body {
                    background-color: #f8f9fa;
                }
                .property-detail-img-wrapper {
                    height: 160px;
                }
                @media (min-width: 768px) {
                    .property-detail-img-wrapper {
                        height: 250px;
                    }
                }
                @media (max-width: 767.98px) {
                    .mobile-list-section {
                        box-shadow: none !important;
                        border-radius: 0 !important;
                        border: none !important;
                        border-bottom: 1px solid rgba(0,0,0,0.08) !important;
                        background: transparent !important;
                        margin-bottom: 24px !important;
                        padding-bottom: 24px !important;
                    }
                    .mobile-list-section .card-body {
                        padding-left: 0 !important;
                        padding-right: 0 !important;
                    }
                    .mobile-list-section:last-child {
                        border-bottom: none !important;
                    }
                }
            `}</style>

            {/* Cancel Booking Custom Modal */}
            {showCancelModal && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="bg-white rounded-4 p-4 p-md-5 shadow-lg position-relative mx-3 mx-md-0" style={{ maxWidth: '400px', width: '100%', animation: 'fadeInUpToast 0.3s ease-out forwards' }}>
                        <div className="d-flex justify-content-center mb-4">
                            <div className="bg-danger bg-opacity-10 text-danger rounded-circle d-flex justify-content-center align-items-center" style={{ width: '64px', height: '64px' }}>
                                <Info size={32} />
                            </div>
                        </div>
                        <h4 className="fw-bold text-center text-dark mb-2">Cancel Booking?</h4>
                        <div className="text-muted text-center mb-4" style={{ fontSize: '14px' }}>
                            Are you sure you want to cancel this booking? This action cannot be undone.
                            <div className="mt-3 text-start bg-light p-3 rounded text-dark small border">
                                <strong>Cancellation Policy:</strong>
                                <ul className="mb-0 mt-1 ps-3">
                                    <li>Cancel 48 hours before check-in for a full refund.</li>
                                    <li>Cancel within 24-48 hours of check-in for a 50% refund.</li>
                                    <li>No refunds for cancellations within 24 hours of check-in.</li>
                                </ul>
                            </div>
                        </div>
                        <div className="d-flex gap-3">
                            <button
                                className="btn btn-light flex-grow-1 fw-bold rounded-pill border"
                                onClick={() => setShowCancelModal(false)}
                                disabled={isCancelling}
                            >
                                No, Keep It
                            </button>
                            <button
                                className="btn btn-danger flex-grow-1 fw-bold rounded-pill"
                                onClick={confirmCancelBooking}
                                disabled={isCancelling}
                            >
                                {isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default BookingDetailPage;
